import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import {
  ErrorOutline as ErrorOutlineIcon,
  WarningAmber as WarningAmberIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../store';
import { formatDateOnly } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';

import { can } from '../../utils/access';
/**
 * App-wide billing banner: unpaid Stripe charge, overdue invoice, or a trial about to end.
 *
 * The backend decides which one applies (payments/services/billing_alerts.py) and sends a
 * single `billing_alert` on the org's subscription_status, so this component only renders.
 * Money alerts are red and carry an action; the trial variant is the same banner in a
 * quieter colour.
 *
 * Only admins and foremen get action buttons: an employee cannot open the Stripe portal
 * (the session is created from the subscription owner's Stripe customer), so a button
 * would dead-end for them.
 */

const severityIcons = {
  error: <ErrorOutlineIcon />,
  warning: <WarningAmberIcon />,
  info: <AccessTimeIcon />,
};

const SUPPORT_EMAIL = 'asiakaspalvelu@verstaani.fi';

function BillingAlertBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentOrganization = useUserStore((state) => state.currentOrganization);

  const alert = currentOrganization?.subscription_status?.billing_alert;
  if (!alert) return null;

  const canManage = can(currentOrganization, 'billing.manage_subscription');
  const orgId = currentOrganization?.organization_id;
  const portalUrl = `/api/payments/stripe-create-portal-session/${orgId ? `?organization=${orgId}` : ''}`;

  let title = '';
  let message = '';
  const actions = [];

  if (alert.type === 'payment_failed') {
    title = t('Subscription payment failed');
    message = t(
      'The subscription payment on {{date}} did not go through. Pay the invoice or update your payment card so your workshop keeps running.',
      { date: formatDateOnly(alert.since) }
    );
    if (canManage) {
      if (alert.payment_url) {
        actions.push({ key: 'pay', label: t('Pay now'), href: alert.payment_url, variant: 'contained', external: true });
      }
      actions.push({
        key: 'portal',
        label: t('Manage Subscription'),
        href: portalUrl,
        variant: alert.payment_url ? 'outlined' : 'contained',
        external: true,
      });
    }
  } else if (alert.type === 'unpaid_invoice') {
    title = t('Invoice unpaid');
    const amount = alert.amount ? formatPriceWithCurrency(alert.amount) : '';
    if (alert.count > 1 && alert.invoice_number && amount) {
      // Not named `count`: that key is reserved by i18next for plural resolution.
      message = t(
        'You have {{invoices}} unpaid Verstaani invoices, {{amount}} in total. The oldest ({{number}}) was due on {{date}}.',
        { invoices: alert.count, amount, number: alert.invoice_number, date: formatDateOnly(alert.since) }
      );
    } else if (alert.invoice_number && amount) {
      message = t(
        'Your Verstaani invoice {{number}} ({{amount}}) was due on {{date}} and is still unpaid. Please pay it so your workshop keeps running.',
        { number: alert.invoice_number, amount, date: formatDateOnly(alert.since) }
      );
    } else {
      // Flagged before the sweep started recording the details (or by an older release).
      message = t(
        'Your Verstaani invoice was due on {{date}} and is still unpaid. Please pay it so your workshop keeps running.',
        { date: formatDateOnly(alert.since) }
      );
    }
    if (canManage) {
      actions.push({
        key: 'support',
        label: t('Contact us'),
        href: `mailto:${SUPPORT_EMAIL}`,
        variant: 'outlined',
      });
    }
  } else if (alert.type === 'trial_ending') {
    title = t('Trial Ending Soon');
    if (alert.days <= 0) {
      message = t('Your free trial ends today. Choose a plan to keep working without a break.');
    } else if (alert.days === 1) {
      message = t('Your free trial ends tomorrow. Choose a plan to keep working without a break.');
    } else {
      message = t('Your free trial ends in {{count}} days. Choose a plan to keep working without a break.', {
        count: alert.days,
      });
    }
    if (canManage) {
      actions.push({
        key: 'plans',
        label: t('Choose Your Plan'),
        variant: 'contained',
        onClick: () => navigate('/settings?tab=billing'),
      });
    }
  } else {
    return null;
  }

  if (!canManage) {
    message = `${message} ${t('Please contact an administrator of your organization.')}`;
  }

  return (
    <Alert
      severity={alert.severity}
      icon={severityIcons[alert.severity]}
      sx={{ mb: 3, alignItems: 'flex-start' }}
    >
      <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>
      <Typography variant="body2">{message}</Typography>
      {actions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {actions.map((action) => (
            <Button
              key={action.key}
              size="small"
              // The primary action carries the alert's own colour so it reads as the
              // thing to do; secondary actions stay neutral.
              color={action.variant === 'contained' ? alert.severity : 'inherit'}
              variant={action.variant}
              href={action.href}
              onClick={action.onClick}
              target={action.external ? '_blank' : undefined}
              rel={action.external ? 'noopener noreferrer' : undefined}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}
    </Alert>
  );
}

export default BillingAlertBanner;
